import { Data, ref } from '@vue/composition-api';
import { Breakpoint, breakpointUp } from '../../../shared/helper/viewport';
import { throttle } from '../../helper/event-control';

/**
 * Provides behaviour for scrolling the document,
 * so the order now bar gets sticky to the top of the screen
 *
 * For mobile we have to trigger the sticky behaviour
 * after our order now button starts touching the mobile header bar, which is 70px.
 *
 * For desktop the trigger is basically the viewport top.
 *
 * @param componentInstance the instance of our component
 * @returns `isSticky` boolean ref, `initScrollBehaviour` function,
 * `destroyScrollBehaviour` function
 */
export const useScrollBehaviour = (componentInstance: {refs: Data}) => {
  /**
   * boolean ref, enables and disables the sticky class in `landingpage__order-now-bar`.
   */
  const isSticky = ref<boolean>(false);

  const onScroll = () => {
    const wrapperRect = (componentInstance.refs.orderNowWrapper as HTMLDivElement)
      .getBoundingClientRect();
    const documentScrollTop = document.body.scrollTop;
    const stickyTriggerOffset = breakpointUp(Breakpoint.xlarge) ? 0 : 70;

    if ((documentScrollTop + stickyTriggerOffset) >= (wrapperRect.top - 16)) {
      // is on top of viewport -> apply sticky class
      isSticky.value = true;
    } else {
      // is in viewport
      isSticky.value = false;
    }
  };

  const onScrollThrottle = throttle(onScroll, 50, true);

  /**
   * initializes scroll behaviour, recommended to use it on mounted
   */
  const initScrollBehaviour = () => {
    document.addEventListener('scroll', onScrollThrottle);
  };

  /**
   * destroys scroll behaviour, recommended to use on unmounted
   */
  const destroyScrollBehaviour = () => {
    document.removeEventListener('scroll', onScrollThrottle);
  };

  return {
    isSticky,
    initScrollBehaviour,
    destroyScrollBehaviour,
  };
};
